// Disclaimer.jsx
import React from "react";
import { motion } from "framer-motion";
import "./Disclaimer.css";
import { useLocalContext } from "../../context/LocalContext";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.65 } },
};

const Disclaimer = () => {
  const { webinfo = {} } = useLocalContext();

  const company = webinfo?.name || "Our Company";
  const email = webinfo?.email || "hello@yourdomain.com";
  const phone = webinfo?.phone || webinfo?.phonecall || "";
  const phoneCall = webinfo?.phonecall || "";

  const items = [
    {
      icon: "i",
      title: "General information",
      text: (
        <>
          The information published on this website by <strong>{company}</strong>{" "}
          is provided for general informational purposes about our digital marketing services. We try to keep
          descriptions accurate, but we make no warranties about completeness, reliability, or fitness for a particular
          use.
        </>
      ),
    },
    {
      icon: "!",
      title: "No professional advice",
      text: (
        <>
          Nothing on this site should be considered legal, financial, medical,
          or professional advice. Always consult qualified professionals for
          guidance specific to your situation.
        </>
      ),
    },
    {
      icon: "✓",
      title: "Use at your own risk",
      text: (
        <>
          Any action you take based on this website is strictly at your own
          risk. <strong>{company}</strong> will not be liable for losses or
          damages arising from use of the site, services, or materials.
        </>
      ),
    },
    {
      icon: "↗",
      title: "External links",
      text: (
        <>
          This website may contain links to third-party websites. We do not
          control their content and do not necessarily endorse it. Visiting
          external links is your choice and responsibility.
        </>
      ),
    },
    {
      icon: "∞",
      title: "Results & offers",
      text: (
        <>
          Marketing performance depends on competition, creative, budget, tracking quality, and platform policies. We do
          not guarantee search rankings, impression share, cost per lead, revenue, or other outcomes. Past results do not
          ensure future performance.
        </>
      ),
    },
    {
      icon: "$",
      title: "Affiliates, ads & sponsorships",
      text: (
        <>
          Some pages may display advertising or include affiliate / referral links. When we earn a commission or receive
          compensation, we label those relationships clearly when required by law (including applicable consumer
          protection guidance). Editorial pages aim to remain accurate regardless of monetization.
        </>
      ),
    },
    {
      icon: "⟳",
      title: "Updates & changes",
      text: (
        <>
          We may update, modify, or remove content at any time without prior
          notice. Continued use of the website means you accept the latest
          version of this disclaimer.
        </>
      ),
    },
  ];

  return (
    <section className="disc" aria-label="Disclaimer">
      <div className="disc__wrap">
        {/* HERO */}
        <motion.div
          className="disc__hero"
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.45 }}
        >
          <div className="disc__badge">
            <span className="dot" aria-hidden="true" />
            Legal notice
          </div>

          <h1 className="disc__title">
            Disclaimer for <span>{company}</span>
          </h1>

          <p className="disc__sub">
            This page explains important limitations for our marketing services, website content, third-party tools, and
            advertising. Please read it alongside our Terms and Privacy Policy.
          </p>
        </motion.div>

        {/* CONTENT */}
        <div className="disc__grid">
          {items.map((it, idx) => (
            <motion.div
              key={idx}
              className="disc__card"
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.25 }}
            >
              <i className="disc__icon" aria-hidden="true">
                {it.icon}
              </i>

              <div>
                <h3 className="disc__h3">{it.title}</h3>
                <p className="disc__p">{it.text}</p>
              </div>
            </motion.div>
          ))}

          {/* CONTACT NOTICE */}
          <motion.div
            className="disc__card disc__card--notice"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
          >
            <i className="disc__icon" aria-hidden="true">
              ✉
            </i>

            <div>
              <h3 className="disc__h3">Questions?</h3>
              <p className="disc__p">
                If you have questions about this disclaimer, contact us at{" "}
                <a href={`mailto:${email}`}>{email}</a>
                {phone ? (
                  <>
                    {" "}
                    or call <strong>{phone}</strong>.
                  </>
                ) : null}
              </p>

              <div className="disc__actions">
                <a className="disc__btn disc__btn--primary" href={`mailto:${email}`}>
                  Email us <span aria-hidden="true">→</span>
                </a>

                {phoneCall ? (
                  <a className="disc__btn disc__btn--ghost" href={`tel:${phoneCall}`}>
                    Call now
                  </a>
                ) : (
                  <a className="disc__btn disc__btn--ghost" href="/contact">
                    Contact page
                  </a>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Disclaimer;